/**
 * iter_195 — Foreground Presence Heartbeat.
 *
 * A single mounted hook that keeps the current user Online while the
 * tab is visible + focused. Sends POST /api/presence/heartbeat every
 * HEARTBEAT_MS milliseconds; PAUSES on `visibilitychange` when the tab
 * goes to the background AND on `blur`, RESUMES on `focus` / return.
 *
 * DB writes are throttled to ≤ 1 per user per 60s server-side (see
 * _stamp_last_active in server.py), so this is safe to run everywhere.
 * The frontend interval is 45s so at least one write lands per throttle
 * window when the tab is active. On resume we also send an immediate
 * ping so Online snaps back the moment the user returns.
 *
 * Only ships heartbeats when a user is authed. If /auth/me returns 401
 * the loop stays idle until the next auth event. No login/logout hooks
 * needed — we just re-check auth on every tick.
 */
import { useEffect, useRef } from "react";
import api from "@/lib/api";

const HEARTBEAT_MS = 45_000;

const isAuthed = () => {
  try {
    return !!(localStorage.getItem("session_token") || document.cookie.match(/session_token=/));
  } catch (_e) {
    return false;
  }
};

export const usePresenceHeartbeat = () => {
  const timerRef = useRef(null);
  const lastSentRef = useRef(0);

  useEffect(() => {
    const ping = async ({ manual = false } = {}) => {
      if (!isAuthed()) return;
      // Client-side throttle so a rapid succession of focus events
      // (Alt-tab, minimize/restore) can never DDoS the endpoint.
      const now = Date.now();
      if (!manual && now - lastSentRef.current < HEARTBEAT_MS - 5000) return;
      lastSentRef.current = now;
      try {
        await api.post("/presence/heartbeat");
      } catch (_e) {
        // Silent — heartbeat failures should never surface to the user.
      }
    };

    const start = () => {
      if (timerRef.current) return;
      timerRef.current = setInterval(ping, HEARTBEAT_MS);
      // Immediate ping on start so the user is Online right away.
      ping({ manual: true });
    };

    const stop = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        start();
      } else {
        stop();
      }
    };

    const onFocus = () => {
      // Snap the user back Online immediately on return without
      // waiting for the next tick.
      if (document.visibilityState === "visible") {
        lastSentRef.current = 0; // bypass client throttle
        ping({ manual: true });
        start();
      }
    };

    const onBlur = () => {
      // Note: blur alone isn't strong enough to stop the loop (the tab
      // may still be visible). We rely on visibilitychange for that.
    };

    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      start();
    }
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    };
  }, []);
};

const PresenceHeartbeat = () => {
  usePresenceHeartbeat();
  return null;
};

export default PresenceHeartbeat;
